import {Directive, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {finalize, take, takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {saveAs} from 'file-saver';
import {DateUtil} from '../../util/DateUtil';
import {ClmRequestBuilder} from 'src/app/modules/clm/services/clm-request-builder';
import {CommunicationTriggerType} from '../../types/communication-trigger-type.enum';
import {CustomValidator} from '../../validators';
import {TableControl} from '../../models/TableControl';
import {EmailDTO} from 'src/app/modules/pbm/models/email/EmailDTO';
import {SharedService} from '../../services/shared-service';
import {EmailService} from 'src/app/modules/pbm/services/email.service';
import {FileUploadService} from '../../services/file-upload.service';


enum TableColumns {
  Icons = 'icons',
  EmailId = 'emailId',
  FromAddress = 'fromAddress',
  ToAddress = 'toAddress',
  CCAddress = 'ccAddress',
  CommunicationTriggerType = 'communicationTriggerType',
  TriggerId = 'triggerId',
  IsSent = 'isSent',
  PdfReport = 'pdf', // FE usage
}

enum IconName {
    Send = 'send',
    Reset = 'reset',
 }
const PAGE_SIZE = 5;

@Directive()
export abstract class BaseEmailClassDirective implements OnInit, OnDestroy {
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly controls = ClmRequestBuilder.getTableControls();
  readonly tableColumns = TableColumns;
  readonly CommunicationTriggerType = CommunicationTriggerType;


  isEditMode: boolean;
  loggedInUserName: string;
  emailOverrideFormControl = new FormControl('', [CustomValidator.email]);
  emailTableControl: TableControl;
  emailExpandedRow: EmailDTO | null;
  emailDataSource: MatTableDataSource<EmailDTO> = new MatTableDataSource<EmailDTO>([]);

  @ViewChild('emailPaginator', {static: false}) emailPaginator: MatPaginator;

  constructor(
    protected emailService: EmailService,
    protected sharedService: SharedService,
    protected fileUploadService: FileUploadService) {
    this.emailTableControl = new TableControl(
      this.controls.length,
      PAGE_SIZE,
      this.controls.pageNumber,
      this.controls.pageSizeOptions,
      TableColumns.EmailId,
      'desc',
      '',
      '');
 }

 // Abstract methods for child class to implement
  protected abstract getListOfEmails(tableControl: TableControl): void;
  protected abstract showIcon(row: EmailDTO, iconName: IconName): boolean;
  protected abstract getToAddress(row: EmailDTO): string;

  get isDataExist(): boolean {
    return !!(this.emailDataSource && this.emailDataSource.data?.length);
 }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getListOfEmails(this.emailTableControl);
 }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
 }

  initSubscriptions(): void {
    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe({
        next: () => {
          this.loggedInUserName = this.sharedService.getLoggedInUserName();
       }
     });
 }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
   }
    if (this.emailPaginator) {
      this.emailPaginator.firstPage();
      this.emailTableControl.pageNumber = 0;
   }

    if ($event.active === TableColumns.CommunicationTriggerType) {
      this.emailTableControl.sortField = 'communicationTriggerTypeByCommunicationTriggerTypeId.name';
   }
    else {
      this.emailTableControl.sortField = $event.active;
   }

    this.emailTableControl.sortDirection = $event.direction;
    this.getListOfEmails(this.emailTableControl);
 }

  pageChanged($event: PageEvent): void {
    this.emailTableControl.pageNumber = $event.pageIndex;
    this.emailTableControl.pageSize = $event.pageSize;
    this.getListOfEmails(this.emailTableControl);
 }

  onClickRow(event: MouseEvent, row: EmailDTO): void {
    const targetElement: HTMLElement = event.target as HTMLElement;
    if (targetElement.classList.contains('row-detail-container')) {
      event.preventDefault();
      event.stopPropagation();

      row.sendEmailChecked = !row.sendEmailChecked;
   }
    else {
      this.emailExpandedRow = this.emailExpandedRow === row ? null : row;
   }
 }

  onClickSendEmailConfirmed(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    if (row.editedToAddress) {
      row.toAddress = row.editedToAddress;
   }

    // Front-end sent emails must override the toAddress always
    row.toAddressManualOverride = true;

    row.emailSentBusyCounter++;
    this.emailService.sendEmail(row)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          row.emailSentBusyCounter--;
          this.onClickToggleIcons(event, row);
       })
      )
      .subscribe({
        next: (_: EmailDTO) => {
          this.sharedService.openSnackBar('Email sent successfully!', 'Dismiss');
          this.getListOfEmails(this.emailTableControl);
       },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error?.businessMessage, 'Dismiss');
       }
     });
 }

  onClickResetEmailConfirmed(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    row.emailSentBusyCounter++;
    this.emailService.resetEmail(row.emailId)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          row.emailSentBusyCounter--;
          this.onClickToggleResetEmailIcons(event, row);
       })
      )
      .subscribe({
        next: (_) => {
          this.sharedService.openSnackBar('Email reset successfully!', 'Dismiss');
          this.getListOfEmails(this.emailTableControl);
       },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error?.businessMessage || `Failed to reset email ${row.emailId}`, 'Dismiss');
       }
     });
 }

  onClickToggleIcons(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    row.sendEmailChecked = !row.sendEmailChecked;
 }

  onClickToggleResetEmailIcons(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    row.resetEmailConfirmation = !row.resetEmailConfirmation;
 }

  onClickGeneratePdfReport($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!row.triggerId || !row.attachment) {
      return;
   }

    const file = row.attachment.file;
    const name = row.attachment.name;

    const blobData = this.fileUploadService.base64toBlob(file, 'data:application/octet-stream;base64');
    const blob = new Blob([blobData]);
    saveAs(blob, name);
 }

  onClickEditAddress($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    row.isEditEmail = true;
    const value: string = row?.editedToAddress ? row.editedToAddress : row.toAddress;
    this.emailOverrideFormControl.patchValue(value);
 }

  onClickCancelEditAddress($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    row.isEditEmail = false;
    row.editedToAddress = '';
    this.emailOverrideFormControl.patchValue('');
    this.emailOverrideFormControl.updateValueAndValidity();
 }

  onClickSaveAddress($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.emailOverrideFormControl.value) {
      this.sharedService.openSnackBar('Provide valid email address', 'Dismiss');
      return;
   }
    else if (this.emailOverrideFormControl.invalid) {
      this.sharedService.openSnackBar('Invalid email address e.g Johndoe@email.com', 'Dismiss');
      return;
   }

    row.editedToAddress = this.emailOverrideFormControl.value;
    row.isEditEmail = false; // close edit mode
 }

  OnClickDuplicate(duplicate: boolean, row: EmailDTO): void {
    if (duplicate) {
      const newEmail: EmailDTO = this.getEmailDTOPayload(row);
      const updatedData: EmailDTO[] = this.emailDataSource.data.slice();
      updatedData.unshift(newEmail);
      this.emailDataSource.data = updatedData;
   }
 }

  private getEmailDTOPayload(originalEmail: EmailDTO): EmailDTO {
    let payload: EmailDTO;
    payload = {
      ...originalEmail,
      emailId: null,
      isSent: 0 // This will enable send/edit icons
   } as EmailDTO;
    return payload;
 }

}
